
import img0 from './Images/OIDC_New_Application.png';
import img1 from './Images/OIDC_Configuration.png';
import img2 from './Images/OIDC_Configuration_Step1.png';
import img3 from './Images/OIDC_Configuration_Step2.png';
import img4 from './Images/OIDC_User_Read_Permission.png';
import img5 from './Images/App_Role_Configuration.png';
import img6 from './Images/OIDC_OnData_Configuration.png';
  
const content = `# Microsoft Entra ID Integration Using OIDC

OnData supports the integration with Identity and Access Management (IAM) systems for user authentication and authorization, based on the industry standard 
protocols, including SAML 2.0 and OpenID Connect (OIDC) protocols. The purpose of this document is to detail the process of configuring OnData and Microsoft Entra ID 
(formerly Azure Active Directory) using the OpenID Connect protocol, so that the users can login to OnData with their Microsoft Entra ID accounts.

**Note**: When choosing between the SAML 2.0 protocol and the OpenID Connect protocol, please consider whether you would like to enable the "SecureFile File Sharing" 
feature later on. The "SecureFile File Sharing" feature allows users to securely share sensitive documents with both external and internal users leveraging the 
Digital Rights Management (DRM) technology supported by Microsoft Azure Information Protection Service. The "SecureFile File Sharing" feature requires: 1) the OIDC 
protocol is used for Entra ID integration; and 2) the Microsoft Azure Information Protection Service is available with your Office 365 subscription. Please choose the OIDC 
protocol if you would like to enable the "SecureFile File Sharing" feature later on.  

### 1. Prerequisite
a. An Entra ID user account with at least one of the following roles: Global Administrator, Cloud Application Administrator, or Application Administrator  
b. An OnData account with the System Administrator role  

### 2. Entra ID Configuration
a. Login to the Azure Portal, and select Microsoft Entra ID  
b. Select \`Manage\` -> \`App registrations\` from the left menu  
c. Create a new application registration for the OnData Portal application  
* Select \`New registration\` from the top menu  

![Create New Application](${ img0 })

* Use “OnData Portal” as the \`Name\` of the application, choose the option of \`Single tenant only\` for the \`Supported account types\`, 
and configure the \`Redirect URI\` with the \`Web\` platform and copy the \`Login Redirect URL\` from the OnData Portal. Below are the steps to find the \`Login Redirect URL\`:  
  * Login to the OnData Portal as a System Administrator, select the \`Settings\` icon from the top right corner, and select \`Organization\` from the dropdown menu  
  * Select the \`SSO Configuration\` tab  
  * Toggle the switch to \`Enable Single Sign-ON (SSO)\`  
  * Under the \`Protocol\` choice, select the \`OpenID Connect\` option and copy the \`Login Redirect URL\`. And it should be: https://api.ondatahub.com/users/api/sso/oidc/consumer  

![OIDC Configuration](${ img1 })

* Click on \`Register\` to register this application  
* Select \`Overview\` from the left menu after the application registration is created
* Copy the value of the \`Application (client) ID\`, which is needed to configure the \`Client ID\` field at the OnData side  
* Copy the value of the \`Directory (tenant) ID\`, which is needed to configure the \`Well Known Configuration URL\` field at the OnData side  
  
![OIDC Configuration Step 1](${ img2 })  
  
d. Create a client secret for the application  
* Select \`Manage\` -> \`Certificates & secrets\` from the left menu  
* Select the \`Client secrets\` tab and click on \`New client secret\`  
* Add a client secret and copy the secret value. Note: You **must copy the secret value** when the client secret is first created. Client secret values 
cannot be viewed, except for immediately after creation. Be sure to save the secret when created before leaving the page  
  
![OIDC Configuration Step 2](${ img3 })  
  
e. Add the \`User.Read\` permission for the Microsoft Graph API  
* Select \`Manage\` -> \`API permissions\` from the left menu
* Under \`Configured permissions\`, click on the \`Add a permission\` button
* Under the \`Microsoft APIs\` tab, click on the \`Microsoft Graph\`, and click on the \`Delegated permissions\` option
* Search for the \`User.Read\` permission, and select that permission, then click on the \`Add permissions\` button to add the selected permission
* Click on the menu item \`Grant admin consent for <Organization Name>\`, and select the \`Yes\` button to confirm
  
![User Read Permission](${ img4 })  
  
f. Create App Roles for this application  
* Select \`Manage\` -> \`App roles\` from the left menu  
* Click on \`Create app role\` to create the OnData "System Administrator" app role  
* Use "System Administrator" as the \`Display name\`, and select the \`Users/Groups\` option under the \`Allowed member types\`. Enter the role value of 
"SystemAdministrator", which should match the role value of the System Administrator role at the OnData Portal. Enter a description and select 
the "Apply" button to create the role  
  
![App Role Configuration](${ img5 })  
  
* You may follow the same process to create all other app roles that match what you have at the OnData Portal, including the custom roles you have created. 
But you may choose to do that later  

g. Assign App Roles to users and user groups  
* Go back to the Entra ID home page, select \`Manage\` -> \`Enterprise applications\` on the left menu  
* Select the “OnData Portal” application, and on the \`Overview\` page, select \`Assign users and groups\` under the tile named \`1.  Assign users and groups\`  
* Click on \`Add user/group\` to add one or more System Administrator users. Select one or more users from Entra ID, and select the System Administrator role, 
then click on the \`Assign\` button to complete the assignment  
  
### 3. OnData Configuration
a. Login to the OnData Portal as a System Administrator, select the Settings icon from the top right corner, and select Organization from the dropdown menu  
b. Select the SSO Configuration tab  
c. Toggle the switch to \`Enable Single Sign-ON (SSO)\`  
d. Under the Protocol choice, select the \`OpenID Connect\` option  
e. Fill out the \`Well Known Configuration URL\` field with either of the following values:  
- For Azure Global Cloud: https://login.microsoftonline.com/{tenant ID}/v2.0/  
- For Azure Government Cloud: https://login.microsoftonline.us/{tenant ID}/v2.0/  

Please replace {tenant ID} with the value of the \`Directory (tenant) ID\` field from the application overview page at the Azure Portal  
  
f. Fill out the \`Client ID\` field with the value of the \`Application (client) ID\` field from the application overview page  
g. Fill out the \`Client Secret\` field with the Secret Value copied from the Certificates & secrets page when the client secret is created  
h. Ensure the \`Logout Redirect URL\` field is filled with https://us.ondatahub.com  
i. Fill out the \`On Logout, Sign Out IdP at URL\` with either of the following values:  
- For Azure Global Cloud: https://login.microsoftonline.com/{tenant ID}/oauth2/v2.0/logout  
- For Azure Government Cloud: https://login.microsoftonline.us/{tenant ID}/oauth2/v2.0/logout  

Please replace {tenant ID} with the value of the \`Directory (tenant) ID\` field from the application overview page  
  
j. Click on the \`Update SSO Configuration\` button to update the SSO settings  
k. Log out of OnData and login again with a user’s organization email address who has been granted access to the OnData Portal application in Step 2 – Entra ID Configuration above  
l. You should be redirected to the Entra ID for login. After a successful login, you will be redirected back to the OnData Portal  

![OIDC OnData Configuration](${ img6 })


`
  
export default content;
